// src/layouts/SuperVisorLayout.tsx
import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AppUserHeader, TenantSideBar } from '@/components';
import { Box, Drawer } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, useTheme } from '@mui/material/styles';
import { AuthService } from '@/services';
import { useDispatch } from 'react-redux';
import { updateUser } from '@/stores/authSlice';

// Shared shell: full-height column, the rail + content sit side by side, and only
// the content area scrolls. Header height is not hardcoded — the content flexes
// into whatever the header leaves, so the page never overflows the viewport.
const PageContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  overflow: 'hidden',
}));

const MainContent = styled(Box)(() => ({
  display: 'flex',
  backgroundColor: '#F0F0F0',
  flex: 1,
  minHeight: 0,
}));

// Content column sits next to the rail; the collapse override in the sidebar
// widens it to `calc(100% - 74px)` via the adjacent-sibling selector.
const ContentColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  maxWidth: '100%',
  [theme.breakpoints.up('lg')]: {
    maxWidth: 'calc(100% - 248px)',
  },
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  padding: theme.spacing(2, 1.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(2.5, 3.75),
  },
}));

const SuperVisorLayout: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);

  useEffect(() => {
    const getUserData = async () => {
      try {
        const resp = await AuthService.getProfile({
          add: [
            'userDetails',
            'sendgridUser',
            'tenantSetting',
            'company',
            'address',
            'group',
            'microsoftAdminConsent',
            'assignMenu',
            'assignMenu.menu',
            'instagramConfig',
            'facebookConfig',
          ],
        });
        dispatch(updateUser(resp.data));
      } catch (error) {
        console.error('Error:', error);
      }
    };

    void getUserData();
  }, [dispatch]);
  return (
    <PageContainer>
      <MainContent>
        {isCompact ? (
          <Drawer
            open={mobileNavigationOpen}
            onClose={() => setMobileNavigationOpen(false)}
            slotProps={{
              paper: {
                'aria-label': 'Primary navigation',
                sx: { overflow: 'hidden', width: 248 },
              },
            }}
          >
            <TenantSideBar
              mobile
              onClose={() => setMobileNavigationOpen(false)}
              onNavigate={() => setMobileNavigationOpen(false)}
            />
          </Drawer>
        ) : (
          <TenantSideBar />
        )}
        <ContentColumn>
          <AppUserHeader
            onMenuClick={isCompact ? () => setMobileNavigationOpen(true) : undefined}
          />
          <ContentArea>
            <Outlet />
          </ContentArea>
        </ContentColumn>
      </MainContent>
    </PageContainer>
  );
};

export default SuperVisorLayout;
